import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faCashRegister,
  faUser,
  faTimes,
  faChevronRight,
  faBell,
  faShieldAlt,
  faQuestionCircle,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";

const BottomBar = ({ token }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSignOutPopup, setShowSignOutPopup] = useState(false); // State for pop-up
  const [activeLink, setActiveLink] = useState("/virtual-cards");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.pathname || location.pathname === "/") {
      setActiveLink("/virtual-cards");
    } else {
      setActiveLink(location.pathname);
    }
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSignOutClick = () => {
    setShowSignOutPopup(true); // Show confirmation pop-up
  };

  const confirmSignOut = () => {
    localStorage.removeItem("token");
    navigate("/login");
    setIsMenuOpen(false);
    setShowSignOutPopup(false); // Hide confirmation pop-up
  };

  const cancelSignOut = () => {
    setShowSignOutPopup(false); // Hide confirmation pop-up
  };

  const handleLinkClick = (path) => {
    if (activeLink !== path) {
      setActiveLink(path);
    }
    setIsMenuOpen(false);
  };

  if (!token) return null;

  return (
    <>
      {/* Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 w-full bg-[#275b57] z-50 rounded-t-xl">
        <nav className="flex items-center justify-between py-3 text-white w-full">
          <ul className="flex items-center justify-around w-full">
            <button
              className={`flex flex-col items-center ${
                activeLink === "/virtual-cards" ? "text-gray-400" : "text-white"
              } hover:text-gray-400`}
              onClick={() => {
                handleLinkClick("/virtual-cards");
                navigate("/virtual-cards");
              }}
            >
              <FontAwesomeIcon icon={faCreditCard} className="text-xl" />
              <span className="text-xs">Cards</span>
            </button>
            <button
              className={`flex flex-col items-center ${
                activeLink === "/transaction" ? "text-gray-400" : "text-white"
              } hover:text-gray-400`}
              onClick={() => {
                handleLinkClick("/transaction");
                navigate("/transaction");
              }}
            >
              <FontAwesomeIcon icon={faCashRegister} className="text-xl" />
              <span className="text-xs">Transaction</span>
            </button>
            <button
              className={`flex flex-col items-center ${
                activeLink === "account" ? "text-gray-400" : "text-white"
              } hover:text-gray-400`}
              onClick={() => {
                handleLinkClick("account");
                toggleMenu();
              }}
            >
              <FontAwesomeIcon icon={faUser} className="text-xl" />
              <span className="text-xs">Account</span>
            </button>
          </ul>
        </nav>
      </div>

      {/* Full-Screen Side Menu */}
      <div
        className={`fixed top-0 right-0 w-full h-full bg-white z-40 transition-transform duration-300 transform ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="p-4 flex justify-between items-center border-b">
          <h2 className="text-lg font-semibold text-[#275b57]">Manage</h2>
          <FontAwesomeIcon
            icon={faTimes}
            className="text-2xl text-[#275b57] cursor-pointer"
            onClick={toggleMenu}
          />
        </div>

        {/* Menu Content */}
        <div className="p-4">
          <h3 className="text-sm font-medium text-gray-500 uppercase mb-3">Settings</h3>
          <ul className="mb-6 space-y-4">
            <MenuItem
              icon={faUser}
              title="Profile"
              link="/my-profile"
              onClick={toggleMenu}
            />
            <MenuItem
              icon={faShieldAlt}
              title="Change Password"
              link="/passwordchange"
              onClick={toggleMenu}
            />
            <MenuItem
              icon={faBell}
              title="Contact Us"
              link="/aboutus"
              onClick={toggleMenu}
            />
          </ul>

          <h3 className="text-sm font-medium text-gray-500 uppercase mb-3">
            Informations
          </h3>
          <ul className="mb-6 space-y-4">
            <MenuItem
              icon={faQuestionCircle}
              title="TOS"
              link="/tos"
              onClick={toggleMenu}
            />
            <MenuItem
              icon={faShieldAlt}
              title="Privacy"
              link="/privacy-policy"
              onClick={toggleMenu}
            />
            <li className="py-4 flex items-center justify-between border-b">
              <button
                onClick={handleSignOutClick}
                className="flex items-center w-full text-left text-[#275b57]"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 mr-3">
                  <FontAwesomeIcon icon={faSignOutAlt} className="text-lg text-gray-500" />
                </div>
                <span className="flex-1">Sign out</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Confirmation Popup */}
      {showSignOutPopup && (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <h3 className="text-lg font-semibold mb-4">Are you sure you want to sign out?</h3>
            <div className="flex justify-center space-x-4">
              <button
                onClick={confirmSignOut}
                className="bg-red-500 text-white px-4 py-2 rounded"
              >
                Yes
              </button>
              <button
                onClick={cancelSignOut}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MenuItem = ({ icon, title, link, onClick }) => (
  <li className="py-4 flex items-center justify-between border-b">
    <NavLink
      to={link}
      className="flex items-center w-full text-left text-[#275b57]"
      onClick={onClick}
    >
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 mr-3">
        <FontAwesomeIcon icon={icon} className="text-lg text-gray-500" />
      </div>
      <span className="flex-1">{title}</span>
      <FontAwesomeIcon icon={faChevronRight} className="text-gray-400" />
    </NavLink>
  </li>
);

export default BottomBar;
